import type { FormattedInvoicePayload } from "../../formattedInvoice/formattedInvoiceData";
import baseFixture from "../__fixtures__/typed-report.json";
import { assertReportAdmission } from "../nativeConditions.mjs";
import { asArray, asRecord, cloneValue, type DesignerSelection, type JsonRecord } from "./reportDesignerModel";
import { isIssuedTemplatePayload } from "../templates/templateCatalog";

const sessions = new WeakSet<InternalFormulaSession>();
export type InternalFormulaSession = Readonly<{
  kind: "formula";
  initialPayload: FormattedInvoicePayload;
  samples: readonly { id: string; label: string; datasets: FormattedInvoicePayload["datasets"] }[];
  templateId?: string;
}>;
export const FORMULA_TARGETS = ["presentationLabel", "roundedQuantity", "nextDate"] as const;

export function formulaFixture(operational = false): FormattedInvoicePayload {
  const fixture = cloneValue(baseFixture);
  fixture.definitionId = "internal-formula-workspace";
  fixture.metadata.reportName = "Formula workspace - controlled synthetic samples";
  fixture.layout.requiredFeatures = [];
  const source = asRecord(fixture);
  const descriptor = asRecord(asArray(asRecord(source.data).datasets)[0]);
  asArray(descriptor.fields).push(
    { path: "surname", type: "string", nullable: false },
    { path: "nickname", type: "string", nullable: true },
    { path: "adjustment", type: "number", nullable: false },
  );
  asArray(asRecord(source.datasets).records).forEach((row, index) => Object.assign(asRecord(row), {
    surname: index < 2 ? "Lovelace" : "Jones", nickname: index % 2 ? "  Sample name  " : null, adjustment: 0.125,
  }));
  const table = formulaTable(source);
  asArray(table.columns).forEach(column => {
    const item = asRecord(column);
    if (FORMULA_TARGETS.some(id => id === item.id)) {
      delete item.expr;
      item.binding = item.id === "presentationLabel" ? "name" : item.id === "nextDate" ? "date" : "quantity";
    }
  });
  asArray(table.columns).push({ id: "legacyQuantity", label: "Legacy quantity", width: 60, expr: { op: "mul", args: [{ field: "quantity" }, { const: 2 }] } });
  asArray(asRecord(asArray(asRecord(source.layout).bands).find(band => asRecord(band).id === "due")).elements).push({
    id: "presentationNote", type: "text", text: "Internal presentation note", x: 0, y: 0, width: 340, height: 20,
  });
  if (operational) {
    const financial = ["labor", "expenses", "lineTotal"];
    descriptor.fields = asArray(descriptor.fields).filter(field => !financial.includes(String(asRecord(field).path)));
    asRecord(source.data).datasets = asArray(asRecord(source.data).datasets).filter(dataset => asRecord(dataset).id === "records");
    source.datasets = { records: asArray(asRecord(source.datasets).records).map(row => Object.fromEntries(Object.entries(asRecord(row)).filter(([key]) => !financial.includes(key)))) };
    table.columns = asArray(table.columns).filter(column => !financial.includes(String(asRecord(column).id)));
    table.footer = { cells: [{ columnId: "presentationLabel", value: "Displayed quantity" }, { columnId: "roundedQuantity", aggregate: { fn: "sum", field: "quantity", scope: "report" } }] };
    for (const band of asArray(asRecord(source.layout).bands).map(asRecord)) band.elements = asArray(band.elements).filter(element => asRecord(element).id !== "amountDue");
    asRecord(source.metadata).reportName = "Operational parameter workspace - controlled synthetic samples";
  }
  return source as FormattedInvoicePayload;
}

export function createInternalFormulaSession(operational = false): InternalFormulaSession {
  if (process.env.NODE_ENV === "production") throw new Error("Internal formula authoring is unavailable in production.");
  const initialPayload = formulaFixture(operational);
  validateInternalFormulaSource(initialPayload);
  const session: InternalFormulaSession = Object.freeze({ kind: "formula", initialPayload, samples: [] });
  sessions.add(session);
  return session;
}

export function createInternalTemplateSession(payload: FormattedInvoicePayload, templateId: string): InternalFormulaSession {
  if (!isIssuedTemplatePayload(payload, templateId)) throw new Error("Template authoring requires an independently instantiated document from the matching catalog entry.");
  const session: InternalFormulaSession = Object.freeze({ kind: "formula", initialPayload: cloneValue(payload), samples: [], templateId });
  sessions.add(session);
  validateInternalFormulaSource(session.initialPayload, session);
  return session;
}

export function isInternalFormulaSession(value: object | undefined): value is InternalFormulaSession {
  return process.env.NODE_ENV !== "production" && !!value && sessions.has(value as InternalFormulaSession);
}

export function formulaTable(value: unknown): JsonRecord {
  const root = asRecord(value);
  const layout = asRecord(root.renderer ? asRecord(root.renderer).layout : root.layout);
  const band = asArray(layout.bands).map(asRecord).find(item => item.id === "details");
  return asArray(band?.elements).map(asRecord).find(item => item.id === "records") || {};
}

export function formulaColumn(value: unknown, id: string): JsonRecord {
  return asArray(formulaTable(value).columns).map(asRecord).find(item => item.id === id) || {};
}

export function isFormulaTarget(selection: DesignerSelection): selection is Extract<DesignerSelection, { type: "nativeTableColumn" }> {
  return selection.type === "nativeTableColumn" && selection.bandId === "details" && selection.tableId === "records" && !["labor", "expenses", "lineTotal"].includes(selection.columnId);
}

export function formulaTextTarget(value: unknown, selection: DesignerSelection): JsonRecord | undefined {
  if (selection.type !== "nativeElement" || selection.elementIds.length !== 1) return undefined;
  const root = asRecord(value);
  const layout = asRecord(root.renderer ? asRecord(root.renderer).layout : root.layout);
  const band = asArray(layout.bands).map(asRecord).find(item => item.id === selection.bandId);
  return asArray(band?.elements).map(asRecord).find(item => item.id === selection.elementIds[0] && ["text", "field"].includes(String(item.type)) &&
    !["amountDue", "fullAmountDue", "labor", "expenses", "lineTotal"].includes(String(item.id)) &&
    !String(item.binding || "").startsWith("invoiceTotals."));
}

export function formulaEditingContext(value: unknown, selection: DesignerSelection): { payload: FormattedInvoicePayload; targetId: string } {
  if (isFormulaTarget(selection)) return { payload: value as FormattedInvoicePayload, targetId: selection.columnId };
  const target = formulaTextTarget(value, selection);
  if (!target) throw new Error("Select a supported direct text element or presentation column.");
  const root = cloneValue(asRecord(value));
  const layout = asRecord(root.renderer ? asRecord(root.renderer).layout : root.layout);
  const band = asArray(layout.bands).map(asRecord).find(item => item.id === (selection as Extract<DesignerSelection, { type: "nativeElement" }>).bandId)!;
  const descriptors = asArray(asRecord(root.data).datasets).map(asRecord);
  const detail = band.role === "detail" ? descriptors.find(item => item.id === band.dataset && item.kind === "collection") : undefined;
  // Transient authoring context, never serialized: the committed carrier remains text.expr.
  layout.bands = [{ id: "details", elements: [{ id: "records", dataset: "records", columns: [{ id: "textFormula", label: target.id, ...(target.expr ? { expr: target.expr } : {}) }] }] }];
  asRecord(root.data).datasets = [...descriptors.filter(item => item.kind === "single"), { id: "records", kind: "collection", fields: detail?.fields || [] }];
  root.datasets = { ...asRecord(root.datasets), records: detail ? asRecord(root.datasets)[String(detail.id)] : [{}] };
  return { payload: root as FormattedInvoicePayload, targetId: "textFormula" };
}

export function validateInternalFormulaSource(value: unknown, session?: InternalFormulaSession) {
  const root = asRecord(value);
  const templateSession = isInternalFormulaSession(session) && session.templateId !== undefined;
  if (root.definitionId !== (templateSession ? session.initialPayload.definitionId : "internal-formula-workspace") || root.schemaVersion !== "reports-v3.1" ||
      (root.renderer !== undefined && root.sourceSchemaVersion !== "reports-v3.1")) throw new Error("Formula authoring requires its controlled native workspace.");
  const admission = assertReportAdmission(value, "validate");
  if (admission.requiredFeatures.some(feature => !["typed-expressions-v1", "report-parameters-v1"].includes(feature))) throw new Error("Formula workspace cannot admit conditional or other mandatory features.");
  const table = formulaTable(value);
  if (table.dataset !== (templateSession ? "invoiceLines" : "records")) throw new Error("Formula workspace dataset is unavailable.");
  const layout = asRecord(root.renderer ? asRecord(root.renderer).layout : root.layout);
  for (const band of asArray(layout.bands).map(asRecord)) {
    for (const element of asArray(band.elements).map(asRecord)) {
      for (const column of asArray(element.columns).map(asRecord)) {
        if (asRecord(column.expr).typed !== undefined && (band.id !== "details" || element.id !== "records" || ["labor", "expenses", "lineTotal", ...(templateSession ? ["amount", "lineExpenses"] : [])].includes(String(column.id)))) throw new Error("Typed authoring cannot replace authoritative financial source columns.");
      }
    }
  }
}

/** Never render typed values here: the controlled Design canvas shows source bindings instead. */
export function formulaDesignProjection(payload: FormattedInvoicePayload): FormattedInvoicePayload {
  const next = cloneValue(payload);
  const layout = asRecord(next.layout);
  layout.requiredFeatures = [];
  delete layout.parameters;
  delete layout.presentationFilters;
  for (const column of asArray(formulaTable(next).columns).map(asRecord)) {
    if (asRecord(column.expr).typed !== undefined) {
      delete column.expr;
      column.binding = "__formulaPreviewUnavailable";
    }
    for (const band of asArray(layout.bands).map(asRecord)) for (const element of asArray(band.elements).map(asRecord)) {
      if (asRecord(element.expr).typed !== undefined) { delete element.expr; element.text = "Typed presentation (Preview to evaluate)"; }
    }
  }
  return next;
}
